import { Controller } from '@hotwired/stimulus';
import axios from 'axios';
import { load } from 'recaptcha-v3';
import { getMeta } from "../helpers";

export default class extends Controller {

    setupFormAction(action, actionData) {
        switch (action) {
            case 'toast': {
                const payload = typeof actionData === 'string'
                    ? { message: actionData, variant: 'success', reload: false }
                    : {
                        message: actionData?.message || '',
                        variant: actionData?.variant || 'success',
                        reload: !!actionData?.reload
                    };

                if (payload.reload) {
                    sessionStorage.setItem('pending_toast', JSON.stringify({
                        message: payload.message,
                        variant: payload.variant
                    }));
                    window.location.reload();
                    return;
                }

                document.dispatchEvent(new CustomEvent('gymplus:toast', { detail: payload }));
                this.element.classList.remove('loading');
                break;
            }

            case 'reset_passwords':
                this.element.querySelectorAll('input[type="password"]').forEach((el) => {
                    el.value = '';
                });
                break;

            case 'login':
                if (actionData && actionData.subscribe && window.SubscribeToTopic) {
                    for (const item of actionData.subscribe) {
                        window.SubscribeToTopic.postMessage(item);
                        console.warn('Subscribed to ' + item);
                    }
                }

                if (actionData && actionData.redirect) {
                    window.location.href = actionData.redirect;
                }
                break;

            case 'logout':
                if (actionData && actionData.unsubscribe && window.UnsubscribeFromTopic) {
                    for (const item of actionData.unsubscribe) {
                        window.UnsubscribeFromTopic.postMessage(item);
                        console.warn('Unsubscribed from ' + item);
                    }
                }

                if (actionData && actionData.redirect) {
                    window.location.href = actionData.redirect;
                }
                break;

            case 'remove_elements':
                if (actionData.length > 0) {
                    actionData.forEach((el) => {
                        document.getElementById(el).remove();
                    });
                }
                break;

            case 'reset_fields':
                if (actionData) {
                    for (const item of actionData) {
                        this.element.querySelectorAll('[name="' + item + '"]').forEach((el) => {
                            el.value = '';
                            if (el.checked) {
                                el.checked = false;
                            }
                            if (el.type === 'file') {
                                const fileController = Stimulus.getControllerForElementAndIdentifier(el.parentElement.parentElement, 'file');
                                console.log(fileController);
                                fileController.resetFileInput(el.parentElement.parentElement);
                            }
                        });
                    }
                }
                break;

            case 'replace_content':
                if (actionData) {
                    this.element.classList.remove('loading');
                    this.element.innerHTML = actionData;
                }
                break;

            case 'show_popup':
                this.element.classList.remove('loading');

                if (actionData) {
                    const popup = document.getElementById('global-popup');

                    if (popup) {
                        const popupController = Stimulus.getControllerForElementAndIdentifier(popup, 'modal');

                        if (popupController) {
                            const content = popup.querySelector('.modal-inner');
                            content.innerHTML = actionData;
                            popupController.showModal();
                        }
                    }
                }
                break;

            //Project specific cases
            case 'register_workout_success':
                this.element.classList.remove('loading');

                if (actionData.availability && actionData.availability.id && actionData.availability.content) {
                    const container = document.getElementById(actionData.availability.id);

                    if (container) {
                        container.innerHTML = actionData.availability.content;
                    }

                }

                if (actionData.button && actionData.button.id && actionData.button.content) {
                    const container = document.getElementById(actionData.button.id);

                    if (container) {
                        container.innerHTML = actionData.button.content;
                    }
                }
                break;

            case 'refresh_cart':
                if (actionData.hasOwnProperty('cart') && actionData.cart.items.length > 1) {
                    const popup = document.querySelector('.popup__cart--eshop');

                    document.querySelector('.add-to-cart-form .btn.submit').classList.add('d-none');
                    document.querySelector('.add-to-cart-form .btn.continue-shopping').classList.remove('d-none');

                    if (popup) {
                        popup.querySelector('.popup__content--counter').innerHTML = actionData.cart.items.length - 1;
                        popup.classList.remove('d-none');

                        this.setupFormGeneralSuccess(actionData);
                    }
                }
                break;

            case 'inline_verification_code':
                const el = this.element.querySelector('.inline_verification_code');

                if (el) {
                    el.classList.remove('d-none');
                    el.classList.remove('text-success', 'text-error');
                    el.classList.add(actionData.success ? 'text-success' : 'text-error');
                    el.innerHTML = actionData.reason;
                }

                break;

        }
    }

    initialize() {
        this._boundSubmit = this.handleSubmit.bind(this);
        this._turnstilePoll = null;
    }

    connect() {
        const lang = getMeta('wp-language') || 'lt-LT';
        this.currentLanguageCode = lang.split('-')[0];
        this.siteKey = this.element.getAttribute('data-site-key') || undefined;

        this.useStimulusSubmit = this.element.dataset.axiosFormSubmitMode === 'stimulus';

        if (!this.useStimulusSubmit) {
            this.element.addEventListener('submit', this._boundSubmit);
        }

        if (this.element.dataset.axiosFormTurnstileGate === 'true') {
            if (this.hasTurnstileWidget()) {
                this.setSubmitDisabled(true);
            }
            this.setupTurnstileGate();
        } else {
            this.setSubmitDisabled(false);
        }
    }

    disconnect() {
        if (!this.useStimulusSubmit) {
            this.element.removeEventListener('submit', this._boundSubmit);
        }
        if (this._turnstilePoll) {
            clearInterval(this._turnstilePoll);
            this._turnstilePoll = null;
        }
    }

    async handleSubmit(e) {
        e.preventDefault();

        if (this.element.classList.contains('loading')) return;

        const fileSizeError = this.getFileSizeError();
        if (fileSizeError) {
            this.removeFormNotes();
            this.setupFormGeneralError({ general_error: fileSizeError });
            return;
        }

        this.element.classList.add('loading');

        const recaptcha = this.siteKey ? await load(this.siteKey) : undefined;
        const token = recaptcha ? await recaptcha.execute('submit') : undefined;

        await this.submitForm(token);
    }

    async submitForm(token) {
        const endpoint = this.element.dataset.axiosFormEndpoint || this.element.action;
        const url = endpoint + (endpoint.includes('?') ? '&' : '?') + 'rest_language=' + this.currentLanguageCode;
        const method = (this.element.method || 'post').toLowerCase();

        const data = new FormData(this.element);
        if (token) {
            data.append('token', token);
        }

        let response;
        let isSuccess = false;
        let shouldResetTurnstile = false;

        try {
            if (method === 'get') {
                response = await axios.get(url);
            } else if (method === 'post') {
                response = await axios.post(url, data, { headers: { 'Content-Type': 'multipart/form-data' } });
            }

            response = response.data.data;
            isSuccess = true;
        } catch (error) {
            response = error.response.data.data;
            shouldResetTurnstile = true;
        }

        if (isSuccess) {
            this.element.dispatchEvent(new CustomEvent('ajax:success', {
                bubbles: true,
                detail: { response }
            }));
        }

        this.removeFormNotes();

        if (response && response.action) {
            if (Array.isArray(response.action)) {
                response.action.forEach((action) => {
                    this.setupFormAction(action, response.action_data[action]);
                });
            } else {
                this.setupFormAction(response.action, response.action_data);
            }
        }

        //Google events stuff
        var isAppCookieValue = document.cookie.replace(/(?:(?:^|.*;\s*)is_app\s*=\s*([^;]*).*$)|^.*$/, "$1");
        if (window.dataLayer) {
            if (isAppCookieValue === '1') {
                if (response && response.registered) {
                    dataLayer.push({ 'event': 'app_sign_up' });
                }

                if (response && response.logged_in) {
                    dataLayer.push({
                        'event': 'app_login',
                        'user_status': response.user_status
                    });
                }
            } else {
                if (response && response.registered) {
                    dataLayer.push({ 'event': 'sign_up' });
                }

                if (response && response.logged_in) {
                    dataLayer.push({
                        'event': 'login',
                        'user_status': response.user_status
                    });
                }
            }
        }

        if (response && response.errors && Object.keys(response.errors).length) {
            this.setupFormErrors(response.errors);

        } else if (response && response.general_alert) {
            this.element.classList.remove('loading');
            alert(response.general_alert);
            shouldResetTurnstile = true;

        } else if (response && response.general_error) {
            this.setupFormGeneralError(response);
            shouldResetTurnstile = true;

        } else if (response && response.general_success) {
            this.setupFormGeneralSuccess(response);
            shouldResetTurnstile = true;

        } else if (response && response.redirect) {
            if (response.toast) {
                sessionStorage.setItem('pending_toast', JSON.stringify(response.toast));
            }
            window.location.href = response.redirect;

        } else if (response && response.reload) {
            if (response.toast) {
                sessionStorage.setItem('pending_toast', JSON.stringify(response.toast));
            }
            window.location.reload();

        } else {
            if (!response || !response.action) {
                this.setupFormUnexpectedError();
                shouldResetTurnstile = true;
            }
        }

        if (shouldResetTurnstile) {
            this.resetTurnstile();
        }
    }


    getFileSizeError() {
        const fileWrapper = this.element.querySelector('[data-file-max-size-value]');
        if (!fileWrapper) return null;

        const maxSize = Number(fileWrapper.dataset.fileMaxSizeValue);
        let totalSize = 0;

        this.element.querySelectorAll('input[type="file"]').forEach((input) => {
            for (const file of input.files) {
                totalSize += file.size;
            }
        });

        return totalSize > maxSize ? fileWrapper.dataset.fileMaxSizeMessageValue : null;
    }

    setupFormUnexpectedError() {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-error');
        messageNode.innerHTML = document.querySelector('meta[name="global_axios_validator_error"]').content;

        const generalInfoHolder = this.element.querySelector('.general-info-holder');
        if (generalInfoHolder) {
            generalInfoHolder.appendChild(messageNode);
        } else {
            this.element.appendChild(messageNode);
        }

        this.element.classList.remove('loading');
    }

    setupFormGeneralSuccess(response) {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-success');
        messageNode.innerHTML = response.general_success;

        if (response.hide) {
            messageNode.classList.add('p-0-i');

            for (const el of this.element.querySelectorAll('.axios-hide')) {
                el.classList.add('d-none');
            }
        }

        if (response.clear) {
            for (const el of this.element.querySelectorAll('.axios-clear')) {
                el.value = '';
            }
        }

        const generalInfoHolder = this.element.querySelector('.general-info-holder');
        if (generalInfoHolder) {
            generalInfoHolder.appendChild(messageNode);
        } else {
            this.element.appendChild(messageNode);
        }

        this.element.classList.remove('loading');
    }

    setupFormGeneralError(response) {
        const messageNode = document.createElement('span');
        messageNode.classList.add('general-error');
        messageNode.innerHTML = response.general_error;

        const generalInfoHolder = this.element.querySelector('.general-info-holder');
        if (generalInfoHolder) {
            generalInfoHolder.appendChild(messageNode);
        } else {
            this.element.appendChild(messageNode);
        }

        this.element.classList.remove('loading');
    }

    setupFormErrors(errors) {
        let firstInvalidInput = null;
        for (let i = 0, keys = Object.keys(errors), ii = keys.length; i < ii; i++) {
            let input = this.element.querySelector('[name="' + keys[i] + '"]');
            let fakeInput = this.element.querySelector('[data-name="' + keys[i] + '"]');

            if (fakeInput) {
                fakeInput.classList.add('invalid');
            }

            if (input) {
                if (input.classList.contains('tomselected')) {
                    input.parentNode.querySelector('.ts-wrapper').classList.add('invalid');
                }

                if (i === 0) {
                    firstInvalidInput = input;
                }
                input.classList.add('invalid');

                if (!input.classList.contains('no-error')) {
                    const messageNode = document.createElement('span');
                    messageNode.classList.add('input-error');
                    messageNode.innerHTML = errors[keys[i]];

                    if (input.parentNode.classList.contains('input-container')) {
                        input.parentNode.parentNode.appendChild(messageNode);
                    } else {
                        input.parentNode.insertBefore(messageNode, input.parentNode.lastChild);
                    }
                }
            }
        }

        this.element.classList.remove('loading');

        if (firstInvalidInput) {
            firstInvalidInput.parentNode.scrollIntoView(true);
        }

    }

    removeFormNotes() {
        const generalErrors = this.element.querySelectorAll('.general-error');
        for (const el of generalErrors) {
            if (!el.classList.contains('axios-no-remove')) {
                el.remove();
            }
        }

        const generalSuccess = this.element.querySelectorAll('.general-success');
        for (const el of generalSuccess) {
            if (!el.classList.contains('axios-no-remove')) {
                el.remove();
            }
        }

        const fields = this.element.querySelectorAll('.input, select, input');
        for (const el of fields) {
            el.classList.remove('invalid');
        }

        const messageFields = this.element.querySelectorAll('.input-error');
        for (const el of messageFields) {
            el.remove();
        }
    }

    togglePasswordView(e) {
        const passwordField = e.currentTarget.parentNode.querySelector('input');

        if (passwordField.type === 'password') {
            passwordField.type = 'input';
            e.currentTarget.classList.add('show');
        } else {
            passwordField.type = 'password';
            e.currentTarget.classList.remove('show');
        }
    }

    async sendVerificationCode(event) {
        const button = event.currentTarget;

        if (button.classList.contains('loading')) {
            return;
        }

        const inline = button.hasAttribute('data-inline');
        const siteKey = this.element.dataset.siteKey;
        const recaptcha = siteKey ? await load(siteKey) : undefined;
        const token = recaptcha ? await recaptcha.execute('submit') : undefined;

        button.classList.add('loading');
        button.disabled = true;
        button.classList.add('disabled');

        const url = '/wp-json/api/send-verification-code?rest_language=' + this.currentLanguageCode;

        const phoneInput = this.element.querySelector('input[name="phone"]');
        const personalCodeInput = this.element.querySelector('input[name="personal_code"]');
        const membershipPostInput = this.element.querySelector('input[name="membership_post_id"]');

        if (!phoneInput) {

            button.classList.remove('loading');
            button.disabled = false;
            button.classList.remove('disabled');

            return;
        }

        let formData = new FormData;
        formData.append('phone', phoneInput.value);
        formData.append('personal_code', personalCodeInput ? personalCodeInput.value : '');
        formData.append('inline', inline);
        formData.append('membership_post_id', membershipPostInput ? membershipPostInput.value : '');
        formData.append('is_retry', false); // duplicate of checkout-controller sendVerificationCode method where is_retry is required, here it should be false

        if (token) {
            formData.append('token', token);
        }

        this.removeFormNotes();

        axios.post(url, formData).then((response)=>{
            button.classList.remove('loading');
            response = response.data.data;

            if (!response.success) {
                button.disabled = false;
                button.classList.remove('disabled');
            }

            if (response && response.action) {
                if (Array.isArray(response.action)) {
                    response.action.forEach((action) => {
                        this.setupFormAction(action, response.action_data[action]);
                    });
                } else {
                    this.setupFormAction(response.action, response.action_data);
                }
            }

            if (response && response.errors && Object.keys(response.errors).length) {
                this.setupFormErrors(response.errors);

            } else if (response && response.general_alert) {
                alert(response.general_alert);

            } else if (response && response.general_error) {
                this.setupFormGeneralError(response);

            } else if (response && response.general_success) {
                this.setupFormGeneralSuccess(response);

            } else if (response && response.redirect) {
                window.location.href = response.redirect;

            } else if (response && response.reload) {
                window.location.reload();

            } else {
                if (!response || !response.action) {
                    this.setupFormUnexpectedError();
                }
            }

        }).catch((error)=>{
            button.classList.remove('loading');
            const response = error.response.data.data;

            if (response && response.errors && Object.keys(response.errors).length) {
                this.setupFormErrors(response.errors);
            }

            button.disabled = false;
            button.classList.remove('disabled');
        });
    }

    // CloudFlare
    resetTurnstile() {
        if (typeof window !== 'undefined' &&
            window.turnstile &&
            typeof window.turnstile.reset === 'function') {

            try {
                const turnstileWidgets = document.querySelectorAll('.cf-turnstile');
                if (turnstileWidgets.length > 0) {
                    window.turnstile.reset();
                }
            } catch (error) {}
        }
    }

    hasTurnstileWidget() {
        return !!this.element.querySelector('.cf-turnstile');
    }

    getTurnstileToken() {
        const input = this.element.querySelector('input[name="cf-turnstile-response"]');
        return input?.value?.trim() || '';
    }

    setSubmitDisabled(disabled) {
        this.element.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((btn) => {
            btn.disabled = disabled;
            btn.classList.toggle('disabled', disabled);
            btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        });
    }

    setupTurnstileGate() {
        if (!this.hasTurnstileWidget()) return;
        this.setSubmitDisabled(!this.getTurnstileToken());

        this._turnstilePoll = setInterval(() => {
            if (this.element.classList.contains('loading')) return;
            const hasToken = !!this.getTurnstileToken();
            this.setSubmitDisabled(!hasToken);
        }, 300);
    }
}
